export type MediaGalleryMode = 'user_profile' | 'venue_official' | 'impression_attach';
export type VenueGalleryStatus = 'visible' | 'hidden_by_venue';

export type MediaItem = {
  id: string;
  url: string;
  thumbnail_url: string | null;
  visit_id: string | null;
  visit_date: string | null;
  impression_id: string | null;
  verified: boolean;
  is_avatar: boolean;
  is_official: boolean;
  is_venue_banner?: boolean;
  venue_gallery_status: 'visible' | 'hidden_by_venue' | string;
  sort_order: number | null;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  exif_stripped: boolean;
  moderation_status: 'pending' | 'approved' | 'rejected' | string;
  visibility: 'public' | 'private' | string;
  created_at: string | null;
  user: null | { id: string; display_name: string | null; avatar_url: string | null };
  venue: null | { id: string; name: string | null };
};

export type MediaPage = {
  data: MediaItem[];
  meta: {
    next_cursor: string | null;
    has_more: boolean;
    visible_total?: number;
  };
};
type CsrfResponse = { csrf_token: string };
type DataEnvelope<T> = { data: T };
type VenueAvatar = { avatar_media_id: string | null; avatar_url: string | null };
export type VenueBanner = {
  banner_media_id: string | null;
  banner_url: string | null;
  moderation_status: 'pending' | 'approved' | 'rejected' | null | string;
};
type RequestErrorPayload = {
  message?: string;
  error?: string | { code?: string; message?: string };
  errors?: Record<string, string[]>;
};

export class MediaGalleryApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly fields: Record<string, string[]>;
  constructor(status: number, payload: RequestErrorPayload | null) {
    const nested = typeof payload?.error === 'object' ? payload.error.message : null;
    const fallback = status === 403
      ? 'Публикация фото и изменение аватара временно ограничены.'
      : `Media API failed: ${status}`;
    super(nested ?? payload?.message ?? (typeof payload?.error === 'string' ? payload.error : fallback));
    this.name = 'MediaGalleryApiError';
    this.status = status;
    this.code = typeof payload?.error === 'object' ? payload.error.code ?? null : null;
    this.fields = payload?.errors ?? {};
  }
}

let csrfToken: string | null = null;
const restrictedUploadBatches = new Set<string>();
function emitVenueOfficialMediaChanged(): void { window.dispatchEvent(new CustomEvent('hopper:venue-official-media-changed')); }
function markUploadBatchRestricted(uploadBatchId: string | undefined): void {
  if (!uploadBatchId) return;
  restrictedUploadBatches.add(uploadBatchId);
  window.setTimeout(() => restrictedUploadBatches.delete(uploadBatchId), 30_000);
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const response = await fetch('/api/v1/auth/csrf', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  const payload = await response.json().catch(() => ({})) as Partial<CsrfResponse>;
  if (!response.ok || !payload.csrf_token) throw new MediaGalleryApiError(response.status, payload as RequestErrorPayload);
  csrfToken = payload.csrf_token;
  return csrfToken;
}

async function jsonRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await ensureCsrf());
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(url, { ...init, method, credentials: 'same-origin', headers });
  const payload = await response.json().catch(() => null) as T & RequestErrorPayload | null;
  if (!response.ok) throw new MediaGalleryApiError(response.status, payload);
  return payload as T;
}

export function mediaListUrl(mode: MediaGalleryMode, entityId: string, cursor: string | null = null): string {
  const query = new URLSearchParams({ limit: '50' });
  if (cursor) query.set('cursor', cursor);
  if (mode === 'venue_official') return `/api/v1/venue-cabinet/photos/official?${query.toString()}`;
  if (mode === 'user_profile') {
    const base = entityId === 'me' ? '/api/v1/me/photos' : `/api/v1/users/${encodeURIComponent(entityId)}/photos`;
    return `${base}?${query.toString()}`;
  }
  throw new Error('impression_attach has no server list before impression creation');
}

export function fetchMediaPage(mode: Exclude<MediaGalleryMode, 'impression_attach'>, entityId: string, cursor: string | null = null): Promise<MediaPage> {
  return jsonRequest<MediaPage>(mediaListUrl(mode, entityId, cursor));
}

export function fetchVenueCabinetMedia(cursor: string | null = null): Promise<MediaPage> {
  const query = new URLSearchParams({ limit: '50' });
  if (cursor) query.set('cursor', cursor);
  return jsonRequest<MediaPage>(`/api/v1/venue-cabinet/photos?${query.toString()}`);
}

export async function fetchAllOfficialMedia(entityId: string): Promise<MediaItem[]> {
  const result: MediaItem[] = [];
  let cursor: string | null = null;
  for (;;) {
    const page = await fetchMediaPage('venue_official', entityId, cursor);
    result.push(...page.data);
    if (!page.meta.has_more || !page.meta.next_cursor) return result;
    cursor = page.meta.next_cursor;
  }
}

export function fetchVenueAvatar(): Promise<VenueAvatar> {
  return jsonRequest<DataEnvelope<VenueAvatar>>('/api/v1/venue-cabinet/avatar').then((response) => response.data);
}
export function setVenueAvatar(mediaId: string): Promise<VenueAvatar> {
  return jsonRequest<DataEnvelope<VenueAvatar>>('/api/v1/venue-cabinet/avatar', { method: 'POST', body: JSON.stringify({ media_id: mediaId }) }).then((response) => response.data);
}

export function fetchVenueBanner(): Promise<VenueBanner> {
  return jsonRequest<DataEnvelope<VenueBanner>>('/api/v1/venue-cabinet/banner').then((response) => response.data);
}

export function deleteVenueBanner(): Promise<void> {
  return jsonRequest<{ deleted: boolean }>('/api/v1/venue-cabinet/banner', { method: 'DELETE' }).then(() => undefined);
}

export function uploadVenueBanner(file: File, onProgress?: (value: number) => void): Promise<VenueBanner> {
  return new Promise((resolve, reject) => {
    void ensureCsrf().then((csrf) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/v1/venue-cabinet/banner');
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-CSRF-TOKEN', csrf);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onerror = () => reject(new MediaGalleryApiError(0, { message: 'Ошибка сети при загрузке баннера' }));
      xhr.onload = () => {
        let payload: (DataEnvelope<VenueBanner> & RequestErrorPayload) | null = null;
        try { payload = JSON.parse(xhr.responseText) as DataEnvelope<VenueBanner> & RequestErrorPayload; } catch { payload = null; }
        if (xhr.status < 200 || xhr.status >= 300 || !payload?.data) {
          reject(new MediaGalleryApiError(xhr.status, payload));
          return;
        }
        onProgress?.(100);
        resolve(payload.data);
      };
      const form = new FormData();
      form.append('photo', file);
      xhr.send(form);
    }).catch(reject);
  });
}

export function setVenueGalleryStatus(mediaId: string, status: VenueGalleryStatus): Promise<MediaItem> {
  return jsonRequest<DataEnvelope<MediaItem>>(`/api/v1/venue-cabinet/photos/${encodeURIComponent(mediaId)}`, { method: 'PATCH', body: JSON.stringify({ venue_gallery_status: status }) }).then((response) => response.data);
}

export function uploadMediaFile(
  mode: 'user_profile' | 'venue_official' | 'impression_attach',
  file: File,
  options: { visitId?: string; impressionId?: string; uploadBatchId?: string; onProgress?: (value: number) => void } = {},
): Promise<MediaItem> {
  if (options.uploadBatchId && restrictedUploadBatches.has(options.uploadBatchId)) {
    return Promise.reject(new MediaGalleryApiError(403, {
      error: {
        code: 'publication_restricted',
        message: 'Публикация ограничена. Этот и оставшиеся файлы из выбранной группы не отправлялись.',
      },
    }));
  }

  return new Promise((resolve, reject) => {
    void ensureCsrf().then((csrf) => {
      const xhr = new XMLHttpRequest();
      const url = mode === 'venue_official' ? '/api/v1/venue-cabinet/photos' : '/api/v1/media';
      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-CSRF-TOKEN', csrf);
      xhr.upload.onprogress = (event) => { if (event.lengthComputable) options.onProgress?.(Math.round((event.loaded / event.total) * 100)); };
      xhr.onerror = () => reject(new MediaGalleryApiError(0, { message: 'Ошибка сети при загрузке фото' }));
      xhr.onload = () => {
        let payload: (DataEnvelope<MediaItem> & RequestErrorPayload) | null = null;
        try { payload = JSON.parse(xhr.responseText) as DataEnvelope<MediaItem> & RequestErrorPayload; } catch { payload = null; }
        if (xhr.status < 200 || xhr.status >= 300 || !payload?.data) {
          const error = new MediaGalleryApiError(xhr.status, payload);
          if (error.status === 403 && error.code === 'publication_restricted') {
            markUploadBatchRestricted(options.uploadBatchId);
          }
          reject(error);
          return;
        }
        options.onProgress?.(100);
        if (mode === 'venue_official') emitVenueOfficialMediaChanged();
        resolve(payload.data);
      };
      const form = new FormData();
      form.append('photo', file);
      if (options.uploadBatchId) form.append('upload_batch_id', options.uploadBatchId);
      if (mode === 'user_profile' && options.visitId) form.append('visit_id', options.visitId);
      if (mode === 'impression_attach' && options.impressionId) form.append('impression_id', options.impressionId);
      xhr.send(form);
    }).catch(reject);
  });
}

export async function deleteMedia(mode: 'user_profile' | 'venue_official', mediaId: string): Promise<void> {
  const url = mode === 'venue_official' ? `/api/v1/venue-cabinet/photos/${encodeURIComponent(mediaId)}/official` : `/api/v1/media/${encodeURIComponent(mediaId)}`;
  await jsonRequest<{ deleted: boolean }>(url, { method: 'DELETE' });
  if (mode === 'venue_official') emitVenueOfficialMediaChanged();
}

export async function setAvatar(mediaId: string): Promise<{ avatar_media_id: string; avatar_url: string | null }> {
  const response = await jsonRequest<DataEnvelope<{ avatar_media_id: string; avatar_url: string | null }>>('/api/v1/me/avatar', { method: 'POST', body: JSON.stringify({ media_id: mediaId }) });
  return response.data;
}

export async function reorderOfficialMedia(mediaIds: string[]): Promise<void> {
  await jsonRequest<DataEnvelope<{ media_ids: string[] }>>('/api/v1/venue-cabinet/photos/official/reorder', { method: 'PUT', body: JSON.stringify({ media_ids: mediaIds }) });
}

export async function uploadImpressionFiles(files: File[], impressionId: string, onProgress?: (fileIndex: number, progress: number) => void): Promise<void> {
  const uploadBatchId = files.length > 1 ? crypto.randomUUID() : undefined;
  for (let index = 0; index < files.length; index += 1) {
    await uploadMediaFile('impression_attach', files[index], { impressionId, uploadBatchId, onProgress: (progress) => onProgress?.(index, progress) });
  }
}
